'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useIdPath } from '@/contexts/IdPathProvider';
import { ProcessedMinimap, ProcessedLocation, MarkerType } from '@/types/minimap';
import { useTranslations } from 'next-intl';
import { useLanguage } from '@/contexts/I18nProvider';
import { useKrpano } from "@/contexts/KrpanoProvider";
import { useScene } from "@/templates/Heritage/hooks/useScenes";
import { IconArrowLeft } from "@/templates/Heritage/utils/icons";
import minimapServices from "@/templates/Heritage/utils/services/minimap";
import { MapStack } from "@/templates/Heritage/utils/MapStack";
import { GroupViewCurrentSceneTitle } from "@/templates/Heritage/components/GroupView/CurrentSceneTitle";
interface EnhancedProcessedLocation extends ProcessedLocation {
    marker_type?: string;
    title?: string;
    marker_name?: string;
    icon?: string;
    scale?: number;
    ref?: string;
    code?: string;
}

interface KrpanoInterface {
    call: (cmd: string) => unknown;
    set: (name: string, value: unknown) => void;
    get: (name: string) => unknown;
}

interface EmbedPanoConfig {
    xml: string;
    id: string;
    target: string;
    html5?: string;
    mobilescale?: number;
    passQueryParameters?: boolean;
    security?: string;
    sameorigin?: boolean;
    cross?: boolean;
    onready?: (krpano_interface: KrpanoInterface) => void;
    onerror?: (error: string) => void;
    wmode?: string;
    bgcolor?: string;
}

declare global {
    interface Window {
        embedpano: (config: EmbedPanoConfig) => void;
        changeMap: (mapId: number) => void;
        changeViewByMarker: (sceneId: string, type?: string) => void;
        removepano?: (id: string) => void;
    }
}

interface GenerateMinimapXmlProps {
    forceExpanded?: boolean;
    onMarkerClick?: () => void;
    mapId: string;
}

export default function GenerateMinimapXml({ forceExpanded, onMarkerClick, mapId }: GenerateMinimapXmlProps) {
    const t = useTranslations();
    const { setCurrentScene, currentScene } = useKrpano();
    const { idPath } = useIdPath();
    const { locale } = useLanguage();
    const [expanded, setExpanded] = useState(false);
    const effectiveExpanded = forceExpanded || expanded;
    const { navigateScene } = useScene();

    const [minimapXml, setMinimapXml] = useState<string>('https://sanpham.starglobal3d.com/managements/user_FE/theme/heritage/minimap/minimap-platform.xml');

    const setIsLoading = useCallback((_: boolean = false) => {
    }, []);
    const [initializationError, setInitializationError] = useState<string | null>(null);
    const [noMapData, setNoMapData] = useState<boolean>(false);

    // --- Data and Krpano State ---
    const [minimapData, setMinimapData] = useState<ProcessedMinimap | null>(null);
    const [locations, setLocations] = useState<EnhancedProcessedLocation[]>([]);
    const [markerTypes, setMarkerTypes] = useState<MarkerType[]>([]);
    const [isMinimapReady, setIsMinimapReady] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // --- Refs and History ---
    const minimapPanoRef = useRef<KrpanoInterface | null>(null);
    const targetElementRef = useRef<HTMLDivElement>(null);
    const [mapHistory] = useState(new MapStack());
    const initializationRef = useRef(false); // Prevent multiple initializations
    const initializationCountRef = useRef(0); // Track number of initialization attempts
    const idPathRef = useRef<string | null>(null); // Track idPath changes
    const lastInitializationTime = useRef<number>(0); // Track last initialization time

    // --- Helper Functions ---
    const getCurrentLanguage = useCallback(() => locale || 'vn', [locale]);

    /**
     * Check if XML file exists
     */
    const checkXmlExists = useCallback(async (xmlUrl: string): Promise<boolean> => {
        try {
            const response = await fetch(xmlUrl, { method: 'HEAD' });
            return response.ok;
        } catch (error) {
            console.warn('❌ XML file không tồn tại:', xmlUrl, error);
            return false;
        }
    }, []);

    /**
     * Attempts to recover KRPano instance if it's lost
     */
    const recoverKrpanoInstance = useCallback(async () => {
        // Check if we can recover from existing DOM element
        const existingElement = document.getElementById('minimappano');
        if (existingElement) {
            try {
                // Try to get krpano from global window
                const globalWindow = window as unknown as { krpano?: KrpanoInterface };
                const globalKrpano = globalWindow.krpano;
                if (globalKrpano && typeof globalKrpano.call === 'function') {
                    minimapPanoRef.current = globalKrpano;
                    setIsMinimapReady(true);
                    return true;
                }
            } catch (error) {
                console.warn('⚠️ Failed to recover existing KRPano:', error);
            }
        }

        // If recovery fails, reinitialize
        setInitializationError('timeout');
        return false;
    }, []);

    /**
     * Loads the external KRPano script if it's not already present using safe DOM operations.
     */
    const loadKrpanoScript = useCallback(() => {
        return new Promise<void>((resolve, reject) => {
            // First check if window.embedpano already exists
            if (typeof window.embedpano === 'function') {
                resolve();
                return;
            }

            try {
                // Create script element directly
                const scriptEl = document.createElement('script');
                scriptEl.src = '/krpano-ver1/krpano.js';
                scriptEl.async = true;

                let resolved = false;

                scriptEl.onload = () => {
                    if (!resolved) {
                        resolved = true;
                        resolve();
                    }
                };

                scriptEl.onerror = (error) => {
                    if (!resolved) {
                        resolved = true;
                        console.error('Failed to load Krpano script:', error);
                        reject(new Error('Failed to load Krpano script'));
                    }
                };

                // Safe DOM manipulation with fallbacks
                if (document.head && typeof document.head.appendChild === 'function') {
                    document.head.appendChild(scriptEl);
                } else if (document.body && typeof document.body.appendChild === 'function') {
                    document.body.appendChild(scriptEl);
                } else {
                    throw new Error('No suitable parent element found for script insertion');
                }

                // Backup timeout in case load events don't fire
                setTimeout(() => {
                    if (!resolved) {
                        resolved = true;
                        // Check if embedpano is now available
                        if (typeof window.embedpano === 'function') {
                            resolve();
                        } else {
                            reject(new Error('Krpano script loading timeout'));
                        }
                    }
                }, 8000);

            } catch (error) {
                console.error('❌ Failed to load Krpano script:', error);
                reject(new Error(`Failed to load Krpano: ${error instanceof Error ? error.message : String(error)}`));
            }
        });
    }, []);

    /**
     * Preloads an image to ensure it's in cache.
     */
    const preloadImage = (url: string): Promise<HTMLImageElement> => {

        return new Promise((resolve, reject) => {
            // Check if URL is valid
            if (!url || typeof url !== 'string' || url.trim() === '') {
                console.warn(`⚠️ Invalid image URL: ${url}`);
                reject(new Error(`Invalid image URL: ${url}`));
                return;
            }

            // Create image object
            const img = new Image();

            // Setup event handlers before setting src
            img.onload = () => {
                resolve(img);
            };

            img.onerror = (error) => {
                console.warn(`❌ Image failed to load: ${url}`, error);
                reject(new Error(`Failed to load image: ${url}`));
            };

            // Use a timeout to prevent hanging
            const timeout = setTimeout(() => {
                console.warn(`⏱️ Image load timed out: ${url}`);
                img.src = ''; // Cancel the loading
                reject(new Error(`Image load timed out: ${url}`));
            }, 5000);

            // Clear timeout when image loads
            img.onload = () => {
                clearTimeout(timeout);
                resolve(img);
            };

            // Start loading the image
            try {
                img.src = url;
            } catch (error) {
                clearTimeout(timeout);
                console.error(`❌ Error setting image src: ${url}`, error);
                reject(new Error(`Error setting image src: ${url}`));
            }
        });
    };

    /**
     * Creates a single marker on the KRPano instance.
     */
    const createMarker = useCallback(
        async (item: EnhancedProcessedLocation): Promise<boolean> => {
            let krpano = minimapPanoRef.current;

            // Enhanced check for valid Krpano instance
            if (!krpano || typeof krpano.call !== 'function' || typeof krpano.set !== 'function') {
                console.warn('⚠️ Krpano instance is invalid, attempting recovery...');

                // Try to recover the instance
                const recovered = await recoverKrpanoInstance();
                if (!recovered) {
                    console.error('❌ Failed to recover Krpano instance, marker creation skipped.');
                    return false;
                }

                // Get the recovered instance
                krpano = minimapPanoRef.current;
                if (!krpano) {
                    console.error('❌ Krpano instance still not available after recovery');
                    return false;
                }
            }

            const layerName = `marker_${item.id}`;

            try {
                // Try to add the layer - removed unnecessary delay for better performance
                try {
                    krpano.call(`addlayer(${layerName})`);
                } catch (err) {
                    console.error(`Error adding layer ${layerName}:`, err);
                    return false;
                }

                // Check if the layer was successfully added
                try {
                    krpano.set(`layer[${layerName}].parent`, 'map');
                } catch (err) {
                    console.error(`Layer ${layerName} was not created successfully:`, err);
                    return false;
                }

                // Check for marker type using the extended type
                const markerType = item.marker_name;


                // Use the marker type info to setup the marker
                try {
                    const styleName = markerType;

                    const iconUrl = item.icon.startsWith('https') ? item.icon : `${window.location.origin}/${item.icon}`;
                    // console.log('Setting marker icon URL:', iconUrl, item);
                    // 
                    // krpano.set(`style[${styleName}].url`, item.ref == currentScene ? "https://sanpham.starglobal3d.com/managements/user/img/hotspot_minimap/current-location-25fr-200px.png" : iconUrl);
                    // krpano.set(`style[${styleName}].scale`, item.ref == currentScene ? 1 : item.scale);

                    krpano.set(`style[${styleName}].url`, iconUrl);
                    krpano.set(`style[${styleName}].scale`, item.scale);
                    krpano.call(`layer[${layerName}].loadstyle("${styleName}")`);
                } catch (err) {
                    console.error(`Error setting up marker type for ${layerName}:`, err);
                    try {
                        krpano.call(`removelayer(${layerName})`);
                    } catch (removeErr) {
                        console.error(`Error removing layer ${layerName} after style error:`, removeErr);
                    }
                    return false;
                }

                // Set position and make visible with error handling
                try {
                    krpano.set(`layer[${layerName}].x`, item.x);
                    krpano.set(`layer[${layerName}].y`, item.y);
                    krpano.set(`layer[${layerName}].visible`, true);
                    // Start with full opacity for smoother appearance - prevents choppy animation
                    krpano.set(`layer[${layerName}].alpha`, 1.0);

                    // Set tooltip text (preference to title from API, fallback to marker_name)
                    krpano.set(`layer[${layerName}].tooltip`, item.title || item.marker_name || '');

                    // Set click actions
                    if (item.type === 'map') {
                        const mapIdNum = Number(item.entity_id) || 0;
                        krpano.set(`layer[${layerName}].onclick`, `js(window.changeMap(${mapIdNum}));`);
                    } else if (item.type === 'scene' || item.type === 'view') {
                        const safeRef = JSON.stringify(item.ref || '');
                        krpano.set(`layer[${layerName}].onclick`, `js(window.changeViewByMarker(${safeRef}));`);
                    } else if (item.type === 'link') {
                        const safeRef = JSON.stringify(item.ref || '');
                        krpano.set(`layer[${layerName}].onclick`, `js(window.changeViewByMarker(${safeRef}, 'link'));`);
                    }
                } catch (err) {
                    console.error(`Error setting properties for ${layerName}:`, err);
                    try {
                        krpano.call(`removelayer(${layerName})`);
                    } catch (removeErr) {
                        console.error(`Error removing layer ${layerName} after property error:`, removeErr);
                    }
                    return false;
                }

                return true;
            } catch (error) {
                console.error(`❌ Error creating marker ${layerName}:`, error);
                // Attempt to clean up on error
                try {
                    if (krpano && typeof krpano.call === 'function') {
                        krpano.call(`removelayer(${layerName})`);
                    }
                } catch (removeErr) {
                    console.error(`Error removing layer ${layerName} after error:`, removeErr);
                }
                return false;
            }
        },
        [markerTypes, recoverKrpanoInstance]
    );

    /**
     * Displays a map and its markers on the krpano instance.
     */
    const displayMapAndMarkers = useCallback(
        async (mapData: ProcessedMinimap) => {
            // Wait for krpano to be ready with multiple attempts
            let attempts = 0;
            const maxAttempts = 10;
            while (attempts < maxAttempts) {
                const krpano = minimapPanoRef.current;
                if (krpano && typeof krpano.call === 'function' && typeof krpano.set === 'function') {
                    break;
                }
                attempts++;
                await new Promise((resolve) => setTimeout(resolve, 200));
            }
            const krpano = minimapPanoRef.current;
            if (!krpano) {
                console.error('❌ Krpano instance not available after waiting');
                setInitializationError('timeout');
                return;
            }
            // Verify krpano interface is still valid
            if (typeof krpano.call !== 'function' || typeof krpano.set !== 'function') {
                console.error('❌ Krpano interface is invalid or corrupted');
                setInitializationError('timeout');
                return;
            }
            try {

                try {
                    // Get all layer names
                    const allLayers = Number(krpano.get('layer.count') || 0);
                    for (let i = allLayers - 1; i >= 0; i--) {
                        const layerName = String(krpano.get(`layer[${i}].name`) || '');
                        // If it's a marker, remove it explicitly
                        if (layerName && typeof layerName === 'string' && layerName.startsWith('marker_')) {
                            krpano.call(`removelayer(${layerName});`);
                        }
                    }
                } catch (cleanupError) {
                    console.warn('⚠️ Error during explicit marker cleanup:', cleanupError);
                }

                // Then call removeall to ensure everything is clean
                krpano.call('removeall');
                // Clear any hover effects
                krpano.call('action(hide_hover_image_minimap);');

                // Reset locations array to ensure UI is in sync
                setLocations([]);

                // const baseUrl = window.location.origin;
                // const mapUrl = mapData.url_map.includes('upload') ? `${baseUrl}/${mapData.url_map.startsWith('/') ? mapData.url_map.substring(1) : mapData.url_map}` : `${baseUrl}/upload/minimap/${mapData.url_map}`;


                const baseUrl = window.location.origin;
                const mapUrl = mapData.url_map.startsWith('http')
                    ? mapData.url_map
                    : `${baseUrl}${mapData.url_map.startsWith('/') ? '' : '/'}${mapData.url_map}`;
                // : globalUtils.general.autoCompleteUrl(mapData.url_map);


                // Preload the image first
                try {
                    await preloadImage(mapUrl);
                } catch (imageError) {
                    console.warn('⚠️ Failed to preload map image, continuing anyway:', imageError);
                }

                // Determine map scale based on device
                const isMobile = window.innerWidth <= 768;
                const desktopMapScale = mapData.scale || 0.5;
                const mobileMapScale = mapData.scale_mobile || 0.5;

                // Set map properties with validation
                try {
                    // console.log('Setting map URL:', mapUrl);
                    try {
                        krpano.set('layer[map].onloaded', '');
                        krpano.set('layer[map].onclick', '');
                        krpano.set('layer[map].scalechildren', false);
                        krpano.set('layer[map].align', 'center');
                        krpano.set('layer[map].edge', 'center');
                        krpano.call('layer[map].changeorigin(center, center);');
                        krpano.call('stoptween(layer[map].scale);');
                        krpano.call('stoptween(layer[map].x);');
                        krpano.call('stoptween(layer[map].y);');
                        krpano.set('layer[map].x', 0);
                        krpano.set('layer[map].y', 0);
                    } catch (resetErr) {
                        console.warn('⚠️ Could not reset map layer handlers:', resetErr);
                    }

                    krpano.set('layer[map].url', mapUrl);
                    krpano.set('layer[map].visible', true);

                    if (isMobile) {
                        krpano.set('layer[map].scale', mobileMapScale);
                    } else {
                        krpano.set('layer[map].scale', desktopMapScale);
                    }


                } catch (setError) {
                    console.error('❌ Failed to set map properties:', setError);
                    console.warn('⚠️ Continuing with limited map functionality');
                    // Continue execution rather than throwing
                }

                // Wait for the map to be rendered - reduced for smoother experience
                // await new Promise((resolve) => setTimeout(resolve, 300));

                // Verify krpano is still valid after the delay
                if (!minimapPanoRef.current || typeof minimapPanoRef.current.call !== 'function') {
                    console.warn('⚠️ Krpano interface became invalid during map loading, attempting to continue');
                    // Instead of throwing, we'll try to continue with what we have
                    // Loading effects removed
                    return; // Exit the current function without throwing
                }

                setMinimapData(mapData);
                mapHistory.push({ id: mapData.id, name: mapData.title, map: mapData.url_map });

                // Get current language for the API call
                const currentLanguage = getCurrentLanguage();
                const markerDataReponse = await minimapServices.getMapAndLocation(idPath, mapData.id, currentLanguage);
                const markerData = markerDataReponse.data.locations;
                // console.log('Marker data received:', markerData);
                if (!Array.isArray(markerData)) {
                    console.warn('⚠️ Invalid marker data received, expected array');
                    setLocations([]);
                    return;
                }

                setLocations(markerData);

                if (markerData.length > 0) {
                    // Final check to ensure krpano is still available
                    const krpanoInstance = minimapPanoRef.current;
                    if (krpanoInstance && typeof krpanoInstance.call === 'function') {
                        // On mobile the map layer is scaled down (scale_mobile vs scale).
                        // Markers don't auto-inherit that scale, so we compensate manually.
                        const isMobileMarker = window.innerWidth <= 768;
                        const markerScaleFactor = isMobileMarker
                            ? (mobileMapScale / (desktopMapScale || 1))
                            : 1;
                        const scaledMarkerData = markerScaleFactor !== 1
                            ? markerData.map((marker: any) => ({ ...marker, scale: (marker.scale || 1) * markerScaleFactor }))
                            : markerData;
                        // Create all markers at once without individual delays
                        const markerPromises = scaledMarkerData.map((marker: any) => createMarker(marker));
                        await Promise.all(markerPromises);
                    } else {
                        console.error('❌ Krpano became unavailable during marker creation');
                        console.warn('⚠️ Unable to create markers due to invalid Krpano instance');
                        // Instead of throwing, continue without markers
                    }
                }
            } catch (error) {
                console.error('❌ Error displaying map and markers:', error);
                setInitializationError(`Lỗi khi hiển thị bản đồ: ${error instanceof Error ? error.message : String(error)}`);
            } finally {
                setIsLoading(false);
            }
        },
        [idPath, createMarker, mapHistory, setIsLoading, getCurrentLanguage]
    );

    /**
     * Main setup function. It loads scripts, data, and then embeds KRPano.
     */
    const initializeMinimap = useCallback(async () => {
        // Check for rapid successive calls (debouncing)
        const now = Date.now();
        const timeSinceLastInit = now - lastInitializationTime.current;

        if (timeSinceLastInit < 1000) { // Prevent calls within 1 second
            return;
        }

        // Check if XML file exists (optional check, don't block initialization)
        try {
            const xmlExists = await checkXmlExists(minimapXml);
            if (!xmlExists) {
                console.warn('⚠️ XML file không tồn tại, nhưng vẫn thử khởi tạo:', minimapXml);
                // Không set hasValidXml = false, vẫn cho phép khởi tạo
            }
        } catch (error) {
            console.warn('⚠️ Không thể kiểm tra XML, nhưng vẫn thử khởi tạo:', error);
            // Không block initialization
        }

        // Mark initialization as in progress FIRST and record timestamp
        lastInitializationTime.current = now;
        initializationRef.current = true;
        setIsInitialized(true);
        setInitializationError(null);

        try {
            // Clean up any existing krpano instance first to avoid conflicts
            if (document.getElementById('minimappano')) {
                try {
                    // More efficient cleanup
                    if (minimapPanoRef.current && typeof minimapPanoRef.current.call === 'function') {
                        minimapPanoRef.current.call('removeall');
                    }
                    if (typeof window.removepano === 'function') {
                        window.removepano('minimappano');
                    }
                } catch (cleanupError) {
                    console.warn('Warning during cleanup:', cleanupError);
                }
                // Shorter delay for cleanup to reduce perceived lag
                await new Promise((resolve) => setTimeout(resolve, 300));
            }

            // Check if target element exists and is accessible in DOM
            let targetElement = document.getElementById('minimap_pano');

            // If the target element doesn't exist, try to create it
            if (!targetElement) {
                console.warn('⚠️ Target element #minimap_pano not found, attempting to create it');
                try {
                    const container = document.querySelector('.minimap-container') || document.body;
                    if (container) {
                        targetElement = document.createElement('div');
                        targetElement.id = 'minimap_pano';
                        targetElement.style.width = '100%';
                        targetElement.style.height = '100%';
                        container.appendChild(targetElement);
                    } else {
                        console.error('❌ Could not find container for minimap');
                        setInitializationError('Không thể tìm thấy phần tử để hiển thị bản đồ');
                        setIsLoading(false);
                        return;
                    }
                } catch (error) {
                    console.error('❌ Failed to create #minimap_pano element:', error);
                    setInitializationError('Không thể tạo phần tử để hiển thị bản đồ');
                    setIsLoading(false);
                    return;
                }
            }

            // Load everything in parallel but with proper error handling
            const [scriptsResult] = await Promise.allSettled([loadKrpanoScript()]);
            // const mainMapResponse = await minimapServices.getMainMap(idPath, getCurrentLanguage());
            // const mainMapResult = mainMapResponse.data[0]; 
            const mainMapResponse: any = await minimapServices.getMinimapById(idPath, mapId, getCurrentLanguage());
            const mainMapResult = mainMapResponse.data;

            // Handle script loading
            if (scriptsResult.status === 'rejected') {
                console.error(`❌ Failed to load Krpano script: ${scriptsResult.reason}`);
                setInitializationError('Không thể tải tập lệnh bản đồ');
                setIsLoading(false);
                return; // Exit instead of throwing
            }

            // Handle main map result
            if (mainMapResponse.success === "false") {
                console.error(`❌ Failed to fetch main map: ${mainMapResult.message}`);
                setNoMapData(true);
                setIsLoading(false);
                return; // Exit instead of throwing
            }


            // Use default marker types directly (now just fallback, actual marker info comes from API)
            const markerTypesData: MarkerType[] = [
                {
                    id: 'default',
                    name: 'Default Marker',
                    marker_icon: 'upload/image/minimap/icon-minimap/icon-default.svg',
                    scale: 0.8,
                    style: 'hotspot_minimap-icon_style',
                },
            ];

            setMarkerTypes(markerTypesData);

            const initialMapData = mainMapResult as any;

            // Validate that we have essential data
            if (!initialMapData.id || !initialMapData.url_map) {
                console.error('❌ Main map data is incomplete - missing id or url_map');
                setInitializationError('Dữ liệu bản đồ không đầy đủ');
                setNoMapData(true); // Keep noMapData = true since we don't have valid data
                setIsLoading(false);
                return; // Exit instead of throwing
            }

            // Only set noMapData to false when we have valid data
            setNoMapData(false);

            // Double check that the target element still exists
            if (!document.getElementById('minimap_pano')) {
                console.error('❌ Target element #minimap_pano disappeared before embedding');
                // throw new Error("Target element disappeared before embedding");
            }

            // Setup initialization timeout and promise
            let initializationTimeout: NodeJS.Timeout | null = null;
            let krpanoInitialized = false;

            // Use a more reliable approach with closure variables
            let resolver: (value: KrpanoInterface) => void;
            let rejector: (reason: Error) => void;

            const initializationPromise = new Promise<KrpanoInterface>((resolve, reject) => {
                resolver = resolve;
                rejector = reject;
            });

            // Safe wrappers for callbacks
            const safeResolve = (krpano: KrpanoInterface) => {
                try {
                    if (resolver) resolver(krpano);
                } catch (e) {
                    console.error('Error in promise resolution:', e);
                }
            };

            const safeReject = (error: Error) => {
                try {
                    if (rejector) rejector(error);
                } catch (e) {
                    console.error('Error in promise rejection:', e);
                }
            };

            const onKrpanoReady = (krpano_interface: KrpanoInterface) => {
                // Clear the initialization timeout
                if (initializationTimeout) {
                    clearTimeout(initializationTimeout);
                    initializationTimeout = null;
                }

                // Verify the interface is valid
                if (!krpano_interface || typeof krpano_interface.call !== 'function') {
                    const error = new Error('Invalid Krpano interface received');
                    console.error('❌', error.message);
                    safeReject(error);
                    return;
                }

                // Test the interface with a simple call
                try {
                    krpano_interface.call("trace('Krpano interface test successful')");
                } catch (testError) {
                    console.error('❌ Krpano interface test failed:', testError);
                    return;
                }

                krpanoInitialized = true;
                safeResolve(krpano_interface);
            };

            const onKrpanoError = (error: string) => {
                console.error('❌ KRPano embed error:', error);
                krpanoInitialized = false;

                if (initializationTimeout) {
                    clearTimeout(initializationTimeout);
                    initializationTimeout = null;
                }

                console.warn('⚠️ KRPano embed failed, attempting to recover with limited functionality');

                // Rather than failing completely, we'll set a flag and continue
                // This allows the rest of the UI to work even if the map isn't fully functional
                setInitializationError('Bản đồ có thể không hoạt động đầy đủ chức năng');
                setNoMapData(true); // Show no map message when krpano fails

                // Provide a minimal interface to prevent complete failure
                const minimalInterface = {
                    call: (cmd: string) => {
                        console.warn(`⚠️ Attempted to call Krpano command while in recovery mode: ${cmd}`);
                        return null;
                    },
                    get: (name: string) => {
                        console.warn(`⚠️ Attempted to get Krpano property while in recovery mode: ${name}`);
                        return null;
                    },
                    set: (name: string, value: unknown) => {
                        console.warn(`⚠️ Attempted to set Krpano property while in recovery mode: ${name}=${value}`);
                        return false;
                    }
                } as unknown as KrpanoInterface;

                // Resolve with minimal interface to allow the app to continue
                safeResolve(minimalInterface);
            };

            // Set timeout for initialization
            initializationTimeout = setTimeout(() => {
                if (!krpanoInitialized) {
                    console.warn('⏱️ Krpano initialization timed out after 8 seconds, falling back to static view');
                }
            }, 8000); // 8 seconds timeout

            let embedTargetElement = document.getElementById('minimap_pano');

            // If element is still missing, try creating it one more time
            if (!embedTargetElement) {
                console.warn('⚠️ Target element #minimap_pano disappeared, attempting to recreate');
                try {
                    const container = document.querySelector('.minimap-container') || document.body;
                    if (container) {
                        embedTargetElement = document.createElement('div');
                        embedTargetElement.id = 'minimap_pano';
                        embedTargetElement.style.width = '100%';
                        embedTargetElement.style.height = '100%';
                        container.appendChild(embedTargetElement);
                    } else {
                        console.error('❌ Could not find container for minimap');
                        setInitializationError('Không thể tìm thấy phần tử để hiển thị bản đồ');
                        setIsLoading(false);
                        return;
                    }
                } catch (createError) {
                    console.error('❌ Failed to create #minimap_pano element:', createError);
                    setInitializationError('Không thể tạo phần tử để hiển thị bản đồ');
                    setIsLoading(false);
                    return;
                }
            }

            // Verify embedpano function exists
            if (typeof window.embedpano !== 'function') {
                console.error('❌ embedpano function not available, KRPano script may not be loaded correctly');
                // Try to load the script one more time
                try {
                    await loadKrpanoScript();
                    if (typeof window.embedpano !== 'function') {
                        setInitializationError('Không thể khởi tạo bản đồ');
                        setIsLoading(false);
                        return;
                    }
                } catch (reloadError) {
                    console.error('❌ Failed to reload Krpano script:', reloadError);
                    setInitializationError('Không thể khởi tạo bản đồ');
                    setIsLoading(false);
                    return;
                }
            }

            // Create a safety timeout to detect if XML loading hangs
            // const xmlSafetyTimeout = setTimeout(() => {
            //   console.warn("⚠️ XML loading may be stuck, providing fallback...");

            //   // Try to provide minimal initialization to avoid blocking UI
            //   safeReject(new Error("XML loading timeout"));
            // }, 5000);

            try {
                window.embedpano({
                    // xml: '/krpano-ver1/minimap.xml',
                    xml: minimapXml,
                    id: 'minimappano',
                    target: 'minimap_pano',
                    html5: 'auto',
                    mobilescale: 1.0,
                    // bgcolor: 'rgba(0,0,0,0)', // Đảm bảo nền hoàn toàn trong suốt
                    passQueryParameters: false,
                    wmode: "transparent",
                    security: "allowdomain(*);allowlocalaccess(true);allowscript(true);",
                    sameorigin: false,
                    cross: false,
                    onready: (krpano_interface) => {
                        // clearTimeout(xmlSafetyTimeout);
                        onKrpanoReady(krpano_interface);
                    },
                    onerror: (error) => {
                        // Clear safety timeout since we have an error
                        // clearTimeout(xmlSafetyTimeout);
                        onKrpanoError(error);
                    },
                });

            } catch (embedError) {
                // clearTimeout(xmlSafetyTimeout);
                console.error('❌ Error during embedpano call:', embedError);
                const errorMessage = embedError instanceof Error ? embedError.message : String(embedError);
                console.error('❌ KRPano embed failed:', errorMessage);

                // Trigger the error handler manually
                onKrpanoError(errorMessage);
            }

            try {
                // Wait for Krpano to be ready (or for our minimal interface)
                const krpano_interface = await initializationPromise;

                // Store the interface and update state
                minimapPanoRef.current = krpano_interface;
                setIsMinimapReady(true);

                // Display map with a small delay to ensure everything is ready
                await new Promise((resolve) => setTimeout(resolve, 300));
                await displayMapAndMarkers(initialMapData);
            } catch (initializationError) {
                // If initialization still fails despite our recovery efforts, continue with limited functionality
                console.error('❌ Could not initialize minimap after recovery attempts:', initializationError);
                setInitializationError('Không thể khởi tạo bản đồ');
                setNoMapData(true); // Show no map message when initialization fails
                setIsLoading(false);
            }
        } catch (error) {
            console.error('❌ Error during minimap initialization:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);

            // For timeout errors, show a more user-friendly message
            if (errorMessage.includes('timed out')) {
                setInitializationError('timeout');
            } else {
                setInitializationError(`Lỗi khởi tạo: ${errorMessage}`);
            }
            setNoMapData(true); // Show no map message when any error occurs
            setIsLoading(false);

            // Clean up on error
            minimapPanoRef.current = null;
            setIsMinimapReady(false);
            setIsInitialized(false);
        } finally {
            // Always mark initialization as complete to allow future retries
            initializationRef.current = false;
        }
    }, [idPath, getCurrentLanguage, loadKrpanoScript, displayMapAndMarkers, isMinimapReady, setIsLoading]);

    // Stable reference for initializeMinimap to avoid dependency issues
    const initializeMinimapRef = useRef<(() => Promise<void>) | null>(null);

    // Update the ref whenever the function changes
    useEffect(() => {
        initializeMinimapRef.current = initializeMinimap;
    }, [initializeMinimap]);

    // --- Global Functions for KRPano to Call ---

    const changeMap = useCallback(

        async (mapId: number) => {

            if (!isMinimapReady) return;

            // Notify parent component about marker click if callback is provided
            if (onMarkerClick) {
                onMarkerClick();
            }

            // Set loading state and clear current locations
            setLocations([]);

            try {
                // Clear markers manually before switching maps
                const krpano = minimapPanoRef.current;
                if (krpano && typeof krpano.call === 'function') {
                    // First try to clear all markers explicitly
                    try {
                        krpano.call('removeall');
                    } catch (cleanupError) {
                        console.warn('⚠️ Error during pre-map-change cleanup:', cleanupError);
                    }
                }

                const mapResult = await minimapServices.getMinimapById(idPath, mapId.toString());
                if (mapResult?.data) {
                    const translation = mapResult.data.translations.find((t: { language_id: string }) => t.language_id === getCurrentLanguage());
                    if (translation) {
                        const newMapData: ProcessedMinimap = {
                            id: mapResult.data.id,
                            title: translation.title,
                            url_map: translation.url_map,
                            main_map: mapResult.data.main_map,
                        };
                        await displayMapAndMarkers(newMapData);
                    } else {
                        console.error(`❌ Không tìm thấy ngôn ngữ cho bản đồ ID ${mapId}`);
                        setInitializationError(`Không tìm thấy ngôn ngữ cho bản đồ ID ${mapId}`);
                        // Make sure we clear any existing markers
                        if (minimapPanoRef.current && typeof minimapPanoRef.current.call === 'function') {
                            try {
                                minimapPanoRef.current.call('removeall');
                            } catch (e) {
                                console.warn('⚠️ Error clearing markers on error:', e);
                            }
                        }
                        setIsLoading(false);
                        return; // Return instead of throwing
                    }
                } else {
                    console.error(`❌ Không tìm thấy dữ liệu cho bản đồ ID ${mapId}`);
                    setInitializationError(`Không tìm thấy dữ liệu cho bản đồ ID ${mapId}`);
                    // Make sure we clear any existing markers
                    if (minimapPanoRef.current && typeof minimapPanoRef.current.call === 'function') {
                        try {
                            minimapPanoRef.current.call('removeall');
                        } catch (e) {
                            console.warn('⚠️ Error clearing markers on error:', e);
                        }
                    }
                    setIsLoading(false);
                    return; // Return instead of throwing
                }
            } catch (error) {
                console.error('❌ Error changing map:', error);
                setInitializationError(`Lỗi khi chuyển bản đồ: ${error instanceof Error ? error.message : 'Lỗi không xác định'}`);

                // Clean up markers on error
                if (minimapPanoRef.current && typeof minimapPanoRef.current.call === 'function') {
                    try {
                        minimapPanoRef.current.call('removeall');
                    } catch (e) {
                        console.warn('⚠️ Error clearing markers on error:', e);
                    }
                }

                setIsLoading(false);
            }
        },
        [isMinimapReady, idPath, getCurrentLanguage, displayMapAndMarkers, setIsLoading, onMarkerClick]
    );

    const changeViewByMarker = useCallback((sceneId: string, type?: string) => {
        // Notify parent component about marker click if callback is provided
        // console.log("sceneId", sceneId);    
        if (onMarkerClick) {
            onMarkerClick();
        }

        if (type && type === 'link') {
            window.open(sceneId, '_blank');
        }

        // First try to use the main krpano instance
        const globalWindow = window as unknown as { krpano?: KrpanoInterface };
        const globalKrpano = globalWindow.krpano;

        if (globalKrpano && typeof globalKrpano.call === 'function') {
            try {
                // globalKrpano.call(`loadscene(${sceneId}, null, MERGE, BLEND(1))`);
                navigateScene("to", sceneId);
                setCurrentScene(sceneId);
                return;
            } catch (error) {
                console.warn(`⚠️ Failed to change scene using global krpano:`, error);
            }
        }

        // Fallback: try to use the minimap krpano instance
        const minimapKrpano = minimapPanoRef.current;
        if (minimapKrpano && typeof minimapKrpano.call === 'function') {
            try {
                minimapKrpano.call(`loadscene(${sceneId}, null, MERGE, BLEND(1))`);
                return;
            } catch (error) {
                console.warn(`⚠️ Failed to change scene using minimap krpano:`, error);
            }
        }

        console.warn('⚠️ No available krpano instance found for scene change');
    }, [onMarkerClick]);

    const handleBack = useCallback(async () => {
        if (mapHistory.size() > 1) {
            // Xóa map hiện tại khỏi lịch sử
            mapHistory.pop();
            const previousMapItem = mapHistory.current();

            if (previousMapItem) {
                // Chuyển đến map trước đó. Hàm changeMap sẽ gọi displayMapAndMarkers,
                // và displayMapAndMarkers luôn gọi 'removeall' đầu tiên để xóa marker cũ.
                await changeMap(previousMapItem.id);
            }
        } else {
            console.warn('⚠️ Không thể quay lại - đã ở map đầu tiên');
        }
    }, [mapHistory, changeMap]);

    // --- Effects ---

    // Initial setup effect - only run when idPath changes and hasn't been initialized
    useEffect(() => {
        let mounted = true;

        // Strong duplicate prevention - check all conditions first
        if (!idPath || isInitialized || initializationRef.current) {
            return;
        }

        // Also check if we already have a working instance
        if (minimapPanoRef.current && isMinimapReady) {
            return;
        }

        initializationCountRef.current += 1;

        const initialize = async () => {
            // Update idPath reference
            idPathRef.current = idPath;

            // Final check before proceeding
            if (initializationRef.current || !mounted || isInitialized) {
                return;
            }

            // Mark as initializing
            initializationRef.current = true;

            // Small delay to ensure DOM is fully ready
            await new Promise((resolve) => setTimeout(resolve, 300));

            // Double-check conditions before initializing
            if (mounted && idPathRef.current === idPath && !isInitialized) {
                try {
                    if (initializeMinimapRef.current) {
                        await initializeMinimapRef.current();
                    }
                } catch (error) {
                    console.error('❌ Failed to initialize minimap:', error);
                    if (mounted) {
                        setInitializationError(`Initialization failed: ${error instanceof Error ? error.message : String(error)}`);
                        setIsLoading(false);
                    }
                } finally {
                    // Always reset initialization flag when done
                    if (mounted) {
                        initializationRef.current = false;
                    }
                }
            } else {
                // Reset flag if conditions changed
                initializationRef.current = false;
            }
        };

        initialize();

        // Cleanup function to robustly handle component unmount and React Strict Mode
        return () => {
            mounted = false;

            // Clean up any pending operations
            setIsLoading(false);
            setInitializationError(null);

            // Clean up krpano instance only if we're unmounting or idPath changed
            try {
                if (typeof window.removepano === 'function') {
                    window.removepano('minimappano');
                }
            } catch (err) {
                console.error('❌ Error removing Krpano instance:', err);
            }

            // Reset refs and flags but be careful with state
            minimapPanoRef.current = null;
            initializationRef.current = false;

            // Only reset initialization state if idPath actually changed
            if (idPathRef.current !== idPath) {
                setIsMinimapReady(false);
                setIsInitialized(false);
                setMinimapData(null);
                setLocations([]);
                setMarkerTypes([]);
                initializationCountRef.current = 0;
            }

            idPathRef.current = null;

            // Clean up global functions
            try {
                delete (window as any).changeMap;
                delete (window as any).changeViewByMarker;
            } catch (err) {
                console.warn('Warning cleaning up global functions:', err);
            }
        };
    }, [idPath, setIsLoading]); // Removed isInitialized from dependencies to prevent re-running when it changes

    // Effect to expose functions to the global scope for krpano
    useEffect(() => {
        window.changeMap = changeMap;
        window.changeViewByMarker = changeViewByMarker;
    }, [changeMap, changeViewByMarker]);

    // Function to retry initialization
    const handleRetryInitialization = useCallback(() => {

        // Skip if already initializing
        // if (initializationRef.current) {
        //     console.log('⚠️ Initialization already in progress, ignoring retry request');
        //     return;
        // }

        setInitializationError(null);
        // Only reset noMapData when we're actually retrying to load data
        // setNoMapData(false); // Don't reset here, let it be set to false only when we have valid data

        // Reset initialization state
        setIsInitialized(false);

        // Clean up any previous instance thoroughly before attempting new initialization
        try {
            if (minimapPanoRef.current && typeof minimapPanoRef.current.call === 'function') {
                try {
                    minimapPanoRef.current.call('removeall');
                } catch (e) {
                    console.warn('⚠️ Error cleaning up krpano instance:', e);
                }
            }

            if (window.removepano) {
                window.removepano('minimappano');
            }
        } catch (cleanupError) {
            console.warn('⚠️ Warning during cleanup:', cleanupError);
        }

        minimapPanoRef.current = null;
        setIsMinimapReady(false);

        // Reset data to ensure clean start
        setMinimapData(null);
        setLocations([]);
        setMarkerTypes([]);

        // Use a small delay to ensure DOM is updated before retrying
        setTimeout(() => {
            // Ensure we're still mounted and not already initializing
            if (!initializationRef.current && initializeMinimapRef.current) {
                initializeMinimapRef.current().catch((error) => {
                    console.error('❌ Retry initialization failed:', error);
                    const errorMessage = error instanceof Error ? error.message : String(error);

                    // Handle timeout specifically
                    if (errorMessage.includes('timed out')) {
                        setInitializationError('timeout');
                    } else {
                        setInitializationError(`Retry failed: ${errorMessage}`);
                    }
                    setIsLoading(false);
                });
            }
        }, 500); // Slightly longer delay for stability
    }, [setIsLoading]);

    // Nếu không có dữ liệu map, chỉ hiển thị thông báo
    if (noMapData) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-600 font-medium">{t("MATERIAL_DATA_MISSING")}</p>
            </div>
        );
    }



    return (
        <div className="space-y-4 relative">
            {/* Map Display - Made sticky */}
            {/* <div className="sticky top-0 z-10 shadow-sm mb-0">  */}
            <div className="sticky top-0 z-10 mb-0"> {/* Added sticky positioning, z-index, and a background color */}
                {!effectiveExpanded && mapHistory.size() > 1 && minimapData && minimapData.id !== 1 && (
                    <div className="mb-4 px-4"> {/* Added horizontal padding */}
                        <button onClick={handleBack} className="flex items-center gap-2 px-3 py-1 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-all duration-200">
                            <IconArrowLeft className="h-4 w-4" />
                            Quay lại
                        </button>
                    </div>
                )}

                <div style={{ height: `${!effectiveExpanded && '37vh'}` }} className={`${effectiveExpanded ? 'z-50 flex items-center justify-center' : 'flex items-center justify-center rounded-lg h-[40vh] sm:h-[60vh] md:h-[65vh] 2xl:h-[50vh] p-0 bg-[--gen-minimap-container-bg]'}`}> {/* Added horizontal padding and bottom padding */}
                    <div style={{}} className={`aspect-video relative overflow-hidden ${effectiveExpanded ? 'w-full max-w-5xl h-auto max-h-[80vh]' : 'bg-red h-full w-full'}`}>
                        {/* Nút đóng khi phóng to */}
                        {/* {effectiveExpanded && !hideUI && (
                        <button onClick={() => setExpanded(false)} className="absolute top-3 right-3 z-10 bg-gray-700 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors shadow-lg" aria-label="Đóng bản đồ phóng to">
                            <span className="text-2xl font-bold">×</span>
                        </button>
                    )} */}

                        {/* KRPano Target Element - luôn hiển thị */}
                        <div
                            id="minimap_pano"
                            ref={targetElementRef}
                            className="w-full h-full minimap-container"
                            style={{
                                // minHeight: effectiveExpanded ? '45vh' : '200px',
                                minHeight: effectiveExpanded ? '80%' : '200px',
                                backgroundColor: 'transparent',
                            }}
                        />

                        {/* Overlays */}
                        {initializationError && initializationError !== 'timeout' && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 z-10">
                                <div className="text-center p-4 bg-white/90 rounded-lg shadow-lg">
                                    {initializationError.includes('XML') || initializationError.includes('404') ? (
                                        <>
                                            <div className="mb-4">
                                                <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                                                </svg>
                                            </div>
                                            <p className="text-gray-800 font-medium mb-2">{t("MATERIAL_DATA_MISSING")}</p>
                                            {/* <p className="text-gray-600 text-sm max-w-xs">{t("MAP_WILL_BE_UPDATED_SOON")}</p> */}
                                        </>
                                    ) : (
                                        <>
                                            <div className="w-12 h-12 mx-auto mb-3 text-gray-600">⚠️</div>
                                            <p className="text-gray-800 font-medium mb-2">Lỗi tải bản đồ</p>
                                            <p className="text-gray-600 text-sm max-w-xs">{initializationError}</p>
                                            <button onClick={handleRetryInitialization} className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                                                Thử lại
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* {!effectiveExpanded && !hideUI && minimapData && (
                <div className="mt-3 pb-4">
                    <p className="text-gray-800 font-medium text-center">{minimapData.title}</p>
                </div>
            )} */}
            </div>
        </div>

    );
}
